import { useMemo } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import type { ReportingScope } from '@/domain/reportingCompare';
import { perStudioSeries, singleStudioScope } from '@/domain/reportingCompare';
import { Money } from '@/components/reporting/Money';
import SeededBadge from '@/components/reporting/SeededBadge';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import {
  funnel,
  kpiTiles,
  closeReasonBreakdown,
  customerGeography,
  newVsReturningByDay,
  lapsedRegularsCount,
  reviewsDelta,
} from '@/domain/reportingEngine';

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
}

/**
 * Customers & funnel tab (leak-first reporting, Task 10): where new clients
 * come from, where the base sits, and who's slipping away. Acquisition
 * funnel, guard-rail KPIs, base health tiles, geography, new-vs-returning,
 * close reasons, and the seeded reviews tile.
 */
export default function CustomersFunnelTab({ scope }: { scope: ReportingScope }) {
  const customers = useBrainStore((s) => s.customers);
  const deposits = useBrainStore((s) => s.deposits);
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const marketingCampaigns = useBrainStore((s) => s.marketingCampaigns);
  const sequenceState = useBrainStore((s) => s.sequenceState);
  const studios = useBrainStore((s) => s.studios);
  const { days, studioIds, compareMode, moneyVisible } = scope;

  const single = singleStudioScope(studioIds);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const funnelSteps = useMemo(
    () => funnel(days, single),
    [days, single, customers, deposits, bookings, marketingCampaigns],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const kpis = useMemo(() => kpiTiles(days, single), [days, single, bookings, salePayments]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const closes = useMemo(() => closeReasonBreakdown(single), [single, customers, sequenceState]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const geography = useMemo(() => customerGeography(single), [single, customers]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const newReturningRaw = useMemo(() => newVsReturningByDay(days, single), [days, single, bookings, customers]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lapsed = useMemo(() => lapsedRegularsCount(single), [single, customers, bookings]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reviews = useMemo(() => reviewsDelta(days, single), [days, single, studios]);

  const newReturning = newReturningRaw.map((r) => ({
    day: dayLabel(r.dayIso),
    New: r.newCount,
    Returning: r.returningCount,
  }));
  const newThisWeek = newReturningRaw.reduce((sum, r) => sum + r.newCount, 0);
  const returningThisWeek = newReturningRaw.reduce((sum, r) => sum + r.returningCount, 0);
  const geoMax = Math.max(1, ...geography.map((g) => g.count));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const series = useMemo(
    () => (compareMode ? perStudioSeries(studioIds, (sid) => kpiTiles(days, sid)) : []),
    [compareMode, studioIds, days, bookings, salePayments],
  );

  if (compareMode) {
    return (
      <div className="space-y-4 max-w-6xl" data-testid="reporting-customers">
        <section className="panel p-4 overflow-x-auto">
          <h2 className="heading-serif text-lg text-cream mb-4">Customers, studios compared</h2>
          <table className="w-full text-sm" data-testid="customers-compare-table">
            <thead>
              <tr>
                <th className="text-left label-small-caps pb-2">Metric</th>
                {series.map((s) => (
                  <th key={s.studioId} className="text-right label-small-caps pb-2">
                    {studios[s.studioId]?.name ?? s.studioId}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Avg booking value</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                    <Money amountAud={s.value.avgBookingValueAud} visible={moneyVisible} />
                  </td>
                ))}
              </tr>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Retention rate</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                    {pct(s.value.retentionRate)}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">No-show rate</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                    {pct(s.value.noShowRate)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-customers">
      {/* Reviews - seeded, no live workflow yet */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="panel-dark p-4" data-testid="customers-reviews">
          <p className="label-small-caps flex items-center gap-1">
            Reviews this week
            <SeededBadge label="Reviews — pending live feed" />
            <InfoTip>
              New Google/Facebook reviews landed this window vs the studio&apos;s weekly target. Seeded — there is no
              in-app reviews workflow yet.
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', reviews.meetsTarget ? 'text-mint' : 'text-blood')}>
            {reviews.thisWeek}
            <span className="text-cream-faint text-base"> / {reviews.target}</span>
          </p>
        </div>
      </section>

      {/* Acquisition funnel */}
      <section className="panel p-4" data-testid="customers-funnel">
        <h2 className="heading-serif text-lg text-cream mb-4">
          Acquisition funnel
          <InfoTip>
            Meta ad spend → inquiries (new leads by first touch) → qualified leads → deposits paid → bookings
            completed. Each tile shows the step-to-step conversion.
          </InfoTip>
        </h2>
        <div className="grid grid-cols-5 gap-3">
          {funnelSteps.map((step, idx) => {
            const prev = idx === 0 ? null : funnelSteps[idx - 1];
            const rate = prev && prev.value > 0 ? (step.value / prev.value) * 100 : null;
            return (
              <div key={step.stage} className="panel-dark p-4">
                <p className="label-small-caps flex items-center gap-1">
                  {step.stage}
                  {step.kind === 'spend' ? <SeededBadge label="Ad spend — pending live Marketing API" /> : null}
                </p>
                <p className="font-cinzel text-3xl text-cream mt-2">
                  {step.kind === 'spend' ? (
                    <Money amountAud={step.value} visible={moneyVisible} />
                  ) : (
                    step.value.toLocaleString('en-AU')
                  )}
                </p>
                {step.kind === 'count' && rate !== null ? (
                  <p className="font-inter text-[10px] text-gold-bright mt-2 uppercase tracking-widest">
                    {rate.toFixed(0)}% conversion
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      {/* Guard tiles */}
      <section className="grid grid-cols-3 gap-3">
        <Tile label="Avg booking value" info="Revenue ÷ bookings completed in the selected span (checkout sale incl. deposit applied, merch excluded).">
          <Money amountAud={kpis.avgBookingValueAud} visible={moneyVisible} />
        </Tile>
        <Tile
          label="Retention rate"
          info="Share of customers with a completed session who came back for more than one (all-time)."
        >
          {pct(kpis.retentionRate)}
        </Tile>
        <Tile label="No-show rate" info="No-shows ÷ (completed + no-show) sessions in the selected span.">
          {pct(kpis.noShowRate)}
        </Tile>
      </section>

      {/* Base tiles */}
      <section className="grid grid-cols-3 gap-3">
        <Tile
          label="Lapsed regulars"
          info="Customers whose gap since their last completed session now exceeds 1.5× their own historical average gap. The reactivation engine feeds on this count."
        >
          <span className={lapsed > 0 ? 'text-blood' : undefined}>{lapsed}</span>
        </Tile>
        <Tile label="New this week" info="First-ever completed session in the selected range.">
          {newThisWeek.toLocaleString('en-AU')}
        </Tile>
        <Tile label="Returning" info="Completed session where the customer had an earlier one, in the selected range.">
          {returningThisWeek.toLocaleString('en-AU')}
        </Tile>
      </section>

      {/* Geography */}
      <section className="panel p-4" data-testid="customers-geography">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Where customers come from
          <InfoTip>
            Customer count by home area. VIC customers bucket into the five Melbourne compass areas by suburb;
            interstate customers group by their city.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Customer base by region
        </p>
        {geography.length === 0 ? (
          <p className="font-serif text-cream-soft text-sm">No customers yet.</p>
        ) : (
          <ul className="space-y-2">
            {geography.map((g) => (
              <li key={g.region} className="flex items-center gap-3" data-testid={`customers-geo-${g.region}`}>
                <span className="w-28 shrink-0 font-inter text-sm text-cream truncate">{g.region}</span>
                <div className="flex-1 h-4 rounded bg-ink-soft/40 overflow-hidden">
                  <div
                    className="h-full bg-gold-bright/70 rounded"
                    style={{ width: `${(g.count / geoMax) * 100}%` }}
                  />
                </div>
                <span className="w-8 shrink-0 text-right font-mono text-sm text-cream-soft">{g.count}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* New vs returning */}
      <section className="panel p-4" data-testid="customers-new-returning">
        <h2 className="heading-serif text-lg text-cream mb-1">
          New vs returning
          <InfoTip>
            Completed sessions over the selected range, split by whether it was the customer&apos;s first-ever
            completed session (new) or they had an earlier one (returning).
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Completed sessions this week, first-timers vs returning
        </p>
        <ReportBarChart
          data={newReturning}
          xKey="day"
          stacked
          series={[
            { key: 'New', name: 'New', color: REPORT_COLORS.mint },
            { key: 'Returning', name: 'Returning', color: REPORT_COLORS.gold },
          ]}
        />
      </section>

      {/* Close reasons - won vs unresponsive vs genuine lost, from sequence data */}
      <section className="panel p-4" data-testid="customers-close-reasons">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Close reasons
          <InfoTip>
            Of decided leads: won (lead stage booked or completed), did not pick up or respond (sequence-red
            never-reached), and said not interested (sequence-red said-no). Derived from Prompt A sequence data.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Of decided leads: won, did not pick up or respond, said not interested
        </p>
        {closes.total === 0 ? (
          <p className="font-serif text-cream-soft text-sm">No decided leads yet.</p>
        ) : (
          <>
            <div className="flex h-3 rounded overflow-hidden bg-ink-soft/40" data-testid="close-reasons-bar">
              {closes.wonPct > 0 ? (
                <div className="bg-mint" style={{ width: `${closes.wonPct}%` }} title={`Won: ${closes.wonPct.toFixed(0)}%`} />
              ) : null}
              {closes.lightRedPct > 0 ? (
                <div
                  className="bg-gold-bright"
                  style={{ width: `${closes.lightRedPct}%` }}
                  title={`Did not pick up or respond: ${closes.lightRedPct.toFixed(0)}%`}
                />
              ) : null}
              {closes.darkRedPct > 0 ? (
                <div
                  className="bg-blood"
                  style={{ width: `${closes.darkRedPct}%` }}
                  title={`Said not interested: ${closes.darkRedPct.toFixed(0)}%`}
                />
              ) : null}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <CloseTile label="Won" value={closes.won} pct={closes.wonPct} tone="text-mint" />
              <CloseTile
                label="Did not pick up or respond"
                value={closes.lightReds}
                pct={closes.lightRedPct}
                tone="text-gold-bright"
              />
              <CloseTile label="Said not interested" value={closes.darkReds} pct={closes.darkRedPct} tone="text-blood" />
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Tile({ label, info, children }: { label: string; info?: string; children: ReactNode }) {
  return (
    <div className="panel-dark p-4">
      <p className="label-small-caps flex items-center gap-1">
        {label}
        {info ? <InfoTip>{info}</InfoTip> : null}
      </p>
      <p className="font-cinzel text-3xl text-cream mt-2">{children}</p>
    </div>
  );
}

function CloseTile({ label, value, pct, tone }: { label: string; value: number; pct: number; tone: string }) {
  return (
    <div className="panel-dark p-3" data-testid={`close-reason-${label.toLowerCase().replace(/ /g, '-')}`}>
      <p className="label-small-caps">{label}</p>
      <p className={clsx('font-cinzel text-2xl mt-1', tone)}>
        {pct.toFixed(0)}%<span className="text-cream-faint text-base"> · {value}</span>
      </p>
    </div>
  );
}
